import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

export type DeliveryLocation = {
  label: string;
  area?: string;
  city?: string;
  state?: string;
  pincode?: string;
  displayName: string;
  latitude?: number;
  longitude?: number;
};

type DeliveryLocationState = {
  location: DeliveryLocation | null;
  isConfirmed: boolean;
};

export const DELIVERY_LOCATION_STORAGE_KEY = "ecommerce:delivery-location";

const isValidLocation = (value: unknown): value is DeliveryLocation => {
  if (!value || typeof value !== "object") return false;
  const loc = value as DeliveryLocation;
  return typeof loc.displayName === "string" && loc.displayName.trim().length > 0;
};

const loadPersistedState = (): Pick<
  DeliveryLocationState,
  "location" | "isConfirmed"
> => {
  if (typeof window === "undefined") {
    return { location: null, isConfirmed: false };
  }

  try {
    const saved = localStorage.getItem(DELIVERY_LOCATION_STORAGE_KEY);
    if (!saved) return { location: null, isConfirmed: false };

    const parsed = JSON.parse(saved) as {
      location?: DeliveryLocation | null;
      isConfirmed?: boolean;
    };

    if (parsed.location && isValidLocation(parsed.location)) {
      return {
        location: parsed.location,
        // A saved location counts as confirmed unless explicitly marked otherwise.
        isConfirmed: parsed.isConfirmed !== false,
      };
    }
  } catch {
    localStorage.removeItem(DELIVERY_LOCATION_STORAGE_KEY);
  }

  return { location: null, isConfirmed: false };
};

const persisted = loadPersistedState();

const persistState = (state: DeliveryLocationState) => {
  if (typeof window === "undefined") return;

  try {
    if (!state.location) {
      localStorage.removeItem(DELIVERY_LOCATION_STORAGE_KEY);
      return;
    }

    localStorage.setItem(
      DELIVERY_LOCATION_STORAGE_KEY,
      JSON.stringify({
        location: state.location,
        isConfirmed: state.isConfirmed,
      }),
    );
  } catch {
    // ignore persistence failures
  }
};

const initialState: DeliveryLocationState = {
  location: persisted.location,
  isConfirmed: persisted.isConfirmed,
};

const deliveryLocationSlice = createSlice({
  name: "deliveryLocation",
  initialState,
  reducers: {
    setDeliveryLocation: (state, action: PayloadAction<DeliveryLocation>) => {
      state.location = action.payload;
      persistState(state);
    },
    confirmDeliveryLocation: (state) => {
      state.isConfirmed = true;
      persistState(state);
    },
    hydrateDeliveryLocation: (
      state,
      action: PayloadAction<{
        location: DeliveryLocation | null;
        isConfirmed: boolean;
      }>,
    ) => {
      state.location = action.payload.location;
      state.isConfirmed = action.payload.isConfirmed;
      persistState(state);
    },
    clearDeliveryLocation: (state) => {
      state.location = null;
      state.isConfirmed = false;
      persistState(state);
    },
  },
});

export const {
  setDeliveryLocation,
  confirmDeliveryLocation,
  hydrateDeliveryLocation,
  clearDeliveryLocation,
} = deliveryLocationSlice.actions;

export default deliveryLocationSlice.reducer;
